import { getLong, type Storage } from '../platform/storage'
import { kidPrefix, kids, currentKid } from './children'
import { hiddenStarts, loadEvents } from './events'
import { loadWindows } from './history'
import type { Находка } from './selfcheck-names'

/**
 * Самопроверка устройства при запуске (решение заказчика 28.10.2026).
 *
 * Самые дорогие наши разборы были про тихий разлад: данные лежат целыми и на сервере, и на
 * телефоне, а на экране пусто. Человек видит пустоту и молчит неделю; сервер при этом не
 * видит ничего — обмен-то идёт. Поэтому приложение проверяет себя само: несколько очевидных
 * утверждений о собственном состоянии, которые обязаны быть верны.
 *
 * Нашлось несоответствие — чиним на месте и помечаем. Пометка уезжает в ближайшей посылке,
 * сервер пишет её в журнал, сторож сообщает нам. В пометке только название проверки:
 * ни имени, ни записей, ни чего-либо о ребёнке.
 */

export { НАХОДКИ, type Находка } from './selfcheck-names'

const KEY_FOUND = 'selfcheck_found'

/** Значение похоже на оборванный JSON: начало есть, конца нет. */
function оборвано(raw: string | null): boolean {
  if (raw === null || raw.trim() === '') return false
  if (!/^[[{]/.test(raw.trim())) return false
  try {
    JSON.parse(raw)
    return false
  } catch {
    return true
  }
}

/**
 * Проверяет устройство и чинит, что может. Возвращает названия сработавших проверок.
 *
 * Сама по себе ничего не отправляет: находки складываются в хранилище, а уедут они с
 * ближайшей посылкой обмена — отдельного запроса ради этого делать не стоит.
 */
export async function runSelfCheck(device: Storage, forKid: (id: string) => Storage): Promise<Находка[]> {
  const найдено = new Set<Находка>()
  const список = kids.value
  const текущий = currentKid.value

  if (текущий !== null && список.length > 0 && !список.some((k) => k.id === текущий)) найдено.add('kid_missing')

  if (текущий !== null) {
    const приставка = kidPrefix(текущий)
    const всеКлючи = await device.keys()
    const свои = всеКлючи.filter((k) => k.startsWith(приставка))
    // Пустая ячейка сама по себе не беда: на новом устройстве сразу после входа список
    // малышей уже приехал, а данные ещё нет. Беда — когда данные лежат под другим малышом,
    // а указатель смотрит мимо них: ровно так неделю пропадали окна бодрствования.
    const естьЧужие = всеКлючи.some((k) => k.startsWith('kid.') && !k.startsWith(приставка))
    if (свои.length === 0 && естьЧужие) найдено.add('kid_pointer')

    // Длинные значения: правки «Моего дня» и списки ВБ приезжают с сервера по частям
    const хранилище = forKid(текущий)
    for (const ключ of свои.map((k) => k.slice(приставка.length))) {
      if (!ключ.startsWith('custom_phases_') && !ключ.startsWith('wake_schedules_')) continue
      if (ключ.includes('~') || ключ.includes('updated_at')) continue
      if (оборвано(await getLong(хранилище, ключ))) найдено.add('long_value_broken')
    }

    if (свои.length > 0 && (await журналЗнаетБольше(хранилище))) найдено.add('window_list_gap')
  }

  if (найдено.size > 0) await отметить(device, [...найдено])
  return [...найдено]
}

/**
 * Журнал событий знает окно, которого нет в списке окон.
 *
 * Одно и то же окно лежит у нас дважды: моментом в списке окон и записями в журнале. По
 * списку считаются дни календаря и номер ВБ, по журналу — что в окне происходило. Разойтись
 * они не должны, а сегодня на сервере разошлись: журнал закрыл окно, таблица окон о конце
 * не знала. Копия, тихо отставшая от источника, — причина почти всех наших разборов.
 */
async function журналЗнаетБольше(хранилище: Storage): Promise<boolean> {
  const список = new Set(await loadWindows(хранилище))
  if (список.size === 0) return false
  const журнал = await loadEvents(хранилище)
  const скрытые = hiddenStarts(журнал)
  for (const e of журнал) {
    if (e.deleted === true) continue
    // Ночные пробуждения в списке окон не лежат: это не окна бодрствования
    if (e.kind !== 'phase' && e.kind !== 'window_end') continue
    if (скрытые.has(e.window)) continue
    if (!список.has(e.window)) return true
  }
  return false
}

/**
 * Отметить находку снаружи: так о своей правке сообщает тот, кто её сделал.
 *
 * [подробности] — короткая строка про обстоятельства: что было, что стало, из чего считали.
 * Без неё уведомление говорит «что-то поправили» и разбирать его не с чем (просьба
 * заказчика 29.10.2026). Класть туда можно только числа и метки: ни имени, ни заметок.
 */
export async function markSelfCheckFinding(device: Storage, находка: Находка, подробности?: string): Promise<void> {
  await отметить(device, [находка])
  if (подробности !== undefined) await запомнитьПодробности(device, находка, подробности)
}

/** Сохранить находки до ближайшей посылки. */
async function отметить(device: Storage, находки: Находка[]): Promise<void> {
  const было = await selfCheckFindings(device)
  const все = [...new Set([...было, ...находки])]
  await device.set(KEY_FOUND, JSON.stringify(все))
}

const KEY_WHY = 'selfcheck_why'
/** Длина обрезана: это строка для журнала, а не место для данных. */
const ПРЕДЕЛ = 200

async function запомнитьПодробности(device: Storage, находка: Находка, текст: string): Promise<void> {
  const было = await selfCheckNotes(device)
  await device.set(KEY_WHY, JSON.stringify({ ...было, [находка]: текст.slice(0, ПРЕДЕЛ) }))
}

/** Подробности к находкам: имя находки → строка обстоятельств. */
export async function selfCheckNotes(device: Storage): Promise<Record<string, string>> {
  const raw = await device.get(KEY_WHY)
  if (raw === null) return {}
  try {
    const parsed: unknown = JSON.parse(raw)
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    const out: Record<string, string> = {}
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === 'string') out[k] = v.slice(0, ПРЕДЕЛ)
    }
    return out
  } catch {
    return {}
  }
}

export async function selfCheckFindings(device: Storage): Promise<Находка[]> {
  const raw = await device.get(KEY_FOUND)
  if (raw === null) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed.filter((x) => typeof x === 'string') as Находка[]) : []
  } catch {
    return []
  }
}

/** Пометки уехали — больше о них не напоминаем. */
export async function clearSelfCheckFindings(device: Storage): Promise<void> {
  await device.remove(KEY_FOUND)
  await device.remove(KEY_WHY)
}

export { KEY_FOUND }
